// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Checkbox, Group, NativeSelect, SimpleGrid, Stack, Text, Textarea, TextInput } from '@mantine/core';
import type { SleepConcernCode } from '@polarissleep/shared';
import { SLEEP_CONCERN_SOMETHING_ELSE, SLEEP_CONCERNS, US_STATE_SELECT_OPTIONS } from '@polarissleep/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { useIntakeV3Answers } from './answers';
import { optionalLabel } from './IntakeV3Controls';
import { IntakeV3Frame } from './IntakeV3Frame';
import { ArrowIcon } from './icons';
import { Question } from './Question';
import { ReadOnlyField } from './ReadOnlyField';
import type { IntakeV3StepProps } from './steps';
import type { TimeZoneId, WhereYouLiveAnswers } from './your-details';
import {
  nextSleepConcerns,
  patientVoice,
  resolveHomeState,
  resolveTimeZone,
  TIME_ZONES,
  timeZoneLabel,
  whereYouLiveErrors,
} from './your-details';

const STATE_OPTIONS = [{ value: '', label: 'State', disabled: true }, ...US_STATE_SELECT_OPTIONS];
const TIME_ZONE_OPTIONS = TIME_ZONES.map((zone) => ({ value: zone.value, label: timeZoneLabel(zone.value) }));
const TIME_ZONE_HINT = 'Visit times are shown in this zone.';

/**
 * Home address, time zone and sleep concerns. The home state must be the visit
 * state from eligibility, and the time zone starts as that state's.
 */
export function WhereYouLiveStep({ step, onBack, onContinue }: IntakeV3StepProps): JSX.Element {
  const { answers, updateAnswers } = useIntakeV3Answers();
  const whereYouLive = answers.whereYouLive;
  const visitState = answers.eligibility.state;
  const voice = patientVoice(answers.aboutYou, answers.eligibility.minor);
  const [attempted, setAttempted] = useState(false);
  const [changingTimeZone, setChangingTimeZone] = useState(false);

  const errors = attempted ? whereYouLiveErrors(whereYouLive, visitState) : {};
  const timeZone = resolveTimeZone(whereYouLive, visitState);
  const concerns = whereYouLive.sleepConcerns ?? [];
  const update = (patch: Partial<WhereYouLiveAnswers>): void => updateAnswers('whereYouLive', patch);

  const handleContinue = (): void => {
    setAttempted(true);
    if (Object.keys(whereYouLiveErrors(whereYouLive, visitState)).length === 0) {
      onContinue();
    }
  };

  return (
    <IntakeV3Frame
      currentSection={step.section}
      title={voice.minor ? `Where ${voice.name} lives, and what brings them in` : step.title}
      description={
        voice.minor
          ? 'The home address routes any test kit or prescription. Concerns help the clinician prepare.'
          : 'Your address routes any test kit or prescription. Your concerns help your clinician prepare.'
      }
      onBack={onBack}
      actions={
        <Button rightSection={<ArrowIcon />} onClick={handleContinue}>
          Continue
        </Button>
      }
    >
      <Stack gap={16}>
        <TextInput
          label="Home address"
          autoComplete="address-line1"
          value={whereYouLive.addressLine1 ?? ''}
          onChange={(event) => update({ addressLine1: event.currentTarget.value })}
          error={errors.addressLine1}
        />
        <TextInput
          label={optionalLabel('Address line 2')}
          autoComplete="address-line2"
          value={whereYouLive.addressLine2 ?? ''}
          onChange={(event) => update({ addressLine2: event.currentTarget.value })}
        />
        <SimpleGrid cols={{ base: 1, xs: 3 }} spacing={12} verticalSpacing={16}>
          <TextInput
            label="City"
            autoComplete="address-level2"
            value={whereYouLive.city ?? ''}
            onChange={(event) => update({ city: event.currentTarget.value })}
            error={errors.city}
          />
          <NativeSelect
            label="State"
            autoComplete="address-level1"
            data={STATE_OPTIONS}
            value={resolveHomeState(whereYouLive, visitState) ?? ''}
            onChange={(event) => update({ state: event.currentTarget.value })}
            error={errors.state}
          />
          <TextInput
            label="ZIP code"
            autoComplete="postal-code"
            inputMode="numeric"
            maxLength={10}
            value={whereYouLive.zip ?? ''}
            onChange={(event) => update({ zip: event.currentTarget.value })}
            error={errors.zip}
          />
        </SimpleGrid>
        {changingTimeZone ? (
          <NativeSelect
            label="Time zone"
            description={TIME_ZONE_HINT}
            inputWrapperOrder={['label', 'input', 'description']}
            data={TIME_ZONE_OPTIONS}
            value={timeZone}
            onChange={(event) => {
              update({ timeZone: event.currentTarget.value as TimeZoneId });
              setChangingTimeZone(false);
            }}
          />
        ) : (
          <ReadOnlyField
            label="Time zone"
            value={timeZoneLabel(timeZone)}
            description={TIME_ZONE_HINT}
            actionLabel="Change"
            actionAriaLabel="Change time zone"
            onAction={() => setChangingTimeZone(true)}
          />
        )}

        <Question
          variant="heading"
          label={`What sleep concerns would ${voice.minor ? voice.name : 'you'} like help with?`}
          description="Choose everything that applies."
          error={errors.sleepConcerns}
        >
          <Checkbox.Group
            value={concerns}
            onChange={(value: string[]) =>
              update({ sleepConcerns: nextSleepConcerns(concerns, value as SleepConcernCode[]) })
            }
          >
            <Stack gap={8}>
              {SLEEP_CONCERNS.map((concern) => (
                <Checkbox.Card key={concern.code} value={concern.code} aria-label={concern.display}>
                  <Group wrap="nowrap" gap={12} align="flex-start">
                    <Checkbox.Indicator />
                    <Text fw={600} fz={14} lh={1.45} c="var(--ink-strong)">
                      {concern.display}
                    </Text>
                  </Group>
                </Checkbox.Card>
              ))}
            </Stack>
          </Checkbox.Group>
        </Question>
        {concerns.includes(SLEEP_CONCERN_SOMETHING_ELSE) && (
          <Textarea
            label="Please describe"
            autosize
            minRows={2}
            value={whereYouLive.sleepConcernOther ?? ''}
            onChange={(event) => update({ sleepConcernOther: event.currentTarget.value })}
            error={errors.sleepConcernOther}
          />
        )}
      </Stack>
    </IntakeV3Frame>
  );
}
